"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  ShoppingCart,
  Send,
  ArrowRight,
  User,
  PlayCircle,
} from "lucide-react";
import Link from "next/link";
import Button from "@/components/ui/Button";
import NextSchoolLogo from "@/components/ui/NextSchoolLogo";
import { useCart } from "@/lib/cart";
import { DEFAULT_HEADER, type HeaderConfig, type CtaIcon, type CmsHeaderScope } from "@/lib/cms-types";

const recruitmentNavLinks = [
  { id: "r1", label: "Dlaczego my", href: "#wyroznia" },
  { id: "r2", label: "Co oferujemy", href: "#funkcje" },
  { id: "r3", label: "Jak to działa", href: "#jak-to-dziala" },
  { id: "r4", label: "FAQ", href: "#faq" },
];

interface NavbarProps {
  variant?: "default" | "recruitment";
  config?: HeaderConfig;
  /** Zakres konfiguracji nagłówka — pobiera dedykowany nagłówek landingu. */
  scope?: CmsHeaderScope;
}

const ICONS: Record<Exclude<CtaIcon, "none">, React.ComponentType<{ size?: number }>> = {
  cart: ShoppingCart,
  send: Send,
  arrow: ArrowRight,
  user: User,
  play: PlayCircle,
};

function isInternal(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//");
}

export default function Navbar({ variant = "default", config, scope }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [fetchedConfig, setFetchedConfig] = useState<HeaderConfig | null>(null);
  const { count, toggleCart } = useCart();

  const isRecruitment = variant === "recruitment";
  const activeScope: CmsHeaderScope =
    scope ?? (isRecruitment ? "recruitment" : "global");

  // Klient pobiera konfig, jeśli nie został podany prop-em. Dla landingów
  // (scope != global) pobiera dedykowany, rozwiązany nagłówek z API.
  useEffect(() => {
    if (config) return;
    let cancelled = false;
    const qs = activeScope !== "global" ? `?scope=${activeScope}` : "";
    fetch(`/api/cms${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.header) setFetchedConfig(data.header);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [config, activeScope]);

  const recruitmentFallback: HeaderConfig = {
    navLinks: recruitmentNavLinks,
    ctaButtons: [
      { id: "rcta", label: "Aplikuj", href: "#formularz", variant: "primary", icon: "send" },
    ],
    showCart: false,
    headerMode: "island",
  };

  const activeConfig: HeaderConfig =
    config ?? fetchedConfig ?? (isRecruitment ? recruitmentFallback : DEFAULT_HEADER);

  const navLinks = activeConfig.navLinks;
  const ctaButtons = activeConfig.ctaButtons;
  const showCart = activeConfig.showCart;
  const mode = activeConfig.headerMode ?? "island";

  // ─── Zachowanie przy przewijaniu (wyspa / chowanie) ───────────────
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const hideOnScroll = mode === "island" || mode === "hide";

  useEffect(() => {
    let ticking = false;
    const apply = () => {
      const y = window.scrollY;
      setScrolled(y > 16);
      setHidden(hideOnScroll ? y > lastY.current && y > 140 : false);
      lastY.current = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const raf = requestAnimationFrame(apply);
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [hideOnScroll]);

  // Wygląd „wyspy” (zaokrąglona, odklejona pigułka) vs pełen pasek.
  const floatingLook = mode === "floating" || (mode === "island" && scrolled);
  const isHidden = hidden && !isOpen;

  const renderCta = (
    cta: HeaderConfig["ctaButtons"][number],
    fullWidth = false,
    onClick?: () => void
  ) => {
    const Icon = cta.icon && cta.icon !== "none" ? ICONS[cta.icon] : null;
    const variantMap: Record<string, "primary" | "outline" | "ghost"> = {
      primary: "primary",
      outline: "outline",
      ghost: "ghost",
    };
    const inner = (
      <Button
        size="sm"
        variant={variantMap[cta.variant] ?? "primary"}
        className={`${fullWidth ? "w-full " : ""}inline-flex items-center justify-center gap-2`}
      >
        {Icon && <Icon size={16} />}
        {cta.label}
      </Button>
    );
    const external = !isInternal(cta.href) || cta.openInNewTab;
    if (external) {
      return (
        <a
          key={cta.id}
          href={cta.href}
          target={cta.openInNewTab ? "_blank" : undefined}
          rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
          onClick={onClick}
          className={fullWidth ? "flex-1" : ""}
        >
          {inner}
        </a>
      );
    }
    return (
      <Link key={cta.id} href={cta.href} onClick={onClick} className={fullWidth ? "flex-1" : ""}>
        {inner}
      </Link>
    );
  };

  return (
    <>
      {/* Rezerwacja miejsca, by treść nie chowała się pod stałym paskiem */}
      <div aria-hidden className="h-16 md:h-20" />

      <motion.nav
        initial={{ y: "-130%" }}
        animate={{ y: isHidden ? "-130%" : 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={{ top: "var(--ns-announcement-h, 0px)" }}
        className="fixed inset-x-0 z-50"
      >
        {/* Tło paska na całą szerokość — znika, gdy pasek zwija się w wyspę */}
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-x-0 top-0 h-16 md:h-20 transition-all duration-500 ease-out ${
            floatingLook
              ? "border-b border-transparent bg-transparent shadow-none"
              : "border-b border-slate-200/60 bg-white/90 shadow-sm backdrop-blur-xl"
          }`}
        />

        <div
          className={`relative transition-[padding] duration-500 ease-out ${
            floatingLook ? "px-3 pt-3 sm:px-4" : "px-0 pt-0"
          }`}
        >
          {/* Pasek / pigułka (wyspa) */}
          <div
            className={`mx-auto flex items-center justify-between transition-all duration-500 ease-out ${
              floatingLook
                ? "h-14 max-w-4xl rounded-2xl border border-slate-200/70 bg-white/75 px-4 shadow-xl shadow-slate-900/5 backdrop-blur-2xl md:h-16 md:px-6"
                : "h-16 max-w-7xl border border-transparent px-4 sm:px-6 md:h-20 lg:px-8"
            }`}
          >
            {/* Logo */}
            <Link href="/" className="inline-flex">
              <NextSchoolLogo size="md" variant="dark" href={null} />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) =>
                isInternal(link.href) && !link.openInNewTab ? (
                  <Link
                    key={link.id}
                    href={link.href}
                    className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.id}
                    href={link.href}
                    target={link.openInNewTab ? "_blank" : undefined}
                    rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                    className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                )
              )}
            </div>

            {/* Desktop Right */}
            <div className="hidden lg:flex items-center gap-4">
              {showCart && (
                <button
                  onClick={toggleCart}
                  className="relative p-2 text-slate-600 hover:text-primary transition-colors"
                  aria-label="Koszyk"
                >
                  <ShoppingCart size={20} />
                  {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {count}
                    </span>
                  )}
                </button>
              )}
              {ctaButtons.map((cta) => renderCta(cta))}
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-slate-600"
              aria-label="Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className={`lg:hidden mx-auto overflow-hidden ${
                  floatingLook
                    ? "mt-2 max-w-4xl rounded-2xl border border-slate-200/70 bg-white/95 shadow-xl shadow-slate-900/5 backdrop-blur-2xl"
                    : "max-w-full border-t border-slate-100 bg-white"
                }`}
              >
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex flex-col gap-3">
                    {navLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.href}
                        target={link.openInNewTab ? "_blank" : undefined}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        onClick={() => setIsOpen(false)}
                        className="text-base font-medium text-slate-700 hover:text-primary py-2 transition-colors"
                      >
                        {link.label}
                      </a>
                    ))}
                    <div className="pt-3 border-t border-slate-100 flex items-center gap-3 flex-wrap">
                      {ctaButtons.map((cta) => renderCta(cta, true, () => setIsOpen(false)))}
                      {showCart && (
                        <button
                          onClick={toggleCart}
                          className="relative p-2 text-slate-600"
                          aria-label="Koszyk"
                        >
                          <ShoppingCart size={20} />
                          {count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                              {count}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
    </>
  );
}

